import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import cx from 'classnames'
import _ from 'lodash'

import LegendTable from '../../../../../../../../components/LegendTable'
import CustomDate from '../../../../../../../../components/CustomDate'

import Chart from './components/Chart'

import { isActiveLarge } from './helpers'
import { PRECISION_FORMATS } from './constants'
import { LOCALE_PREFIX } from '../Blocks/PaymentComparison/constants'

import './style.scss'

export default class PaymentComparison extends Component {
    static propTypes = {
        data: PropTypes.object,
        chartData: PropTypes.array,
        originalData: PropTypes.array,
        block: PropTypes.string.isRequired,
        module: PropTypes.string.isRequired,
        id: PropTypes.string.isRequired,
        legend: PropTypes.array,
        translates: PropTypes.shape({
            p12label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            p12sectorLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            dueLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            description: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            interval: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            p12sectorText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            selfColumnText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            suppliersColumnText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
            clubColumnText: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
        }).isRequired,
    }

    getInterval = (legendItem) => {
        if (!legendItem.lowerBound) return 'N/A'

        return `${legendItem.lowerBound} - ${legendItem.upperBound}`
    }

    getP12Value = (isActiveSelf, isActivePartners, isActiveClub) => (
        <div>
            {isActiveSelf && <div className={'multi-value'}>{this.props.translates.selfColumnText}</div>}
            {isActivePartners && <div className={'multi-value'}>{this.props.translates.suppliersColumnText}</div>}
            {isActiveClub && <div className={'multi-value'}>{this.props.translates.clubColumnText}</div>}
        </div>
    )

    getValue = (isActiveSelf, isActivePartners, isActiveClub, isActiveSector) => (
        <div style={{ fontSize: isActiveSelf ? '24px' : '18px' }}>
            {isActiveSelf && <span style={{ color: '#27358b' }}>{_.get(this.props.data, 'subject.paymentIndex')}</span>}
            {isActiveSelf && isActivePartners && <span style={{ color: '#111111' }}>{'/'}</span>}
            {isActivePartners && (
                <span style={{ color: '#66C1ED' }}>{_.get(this.props.data, 'suppliers.paymentIndex')}</span>
            )}
            {(isActivePartners || isActiveSelf) && isActiveClub && <span style={{ color: '#111111' }}>{'/'}</span>}
            {isActiveClub && <span style={{ color: '#d53868' }}>{_.get(this.props.data, 'club.paymentIndex')}</span>}
            {(isActiveClub || isActiveSelf || isActivePartners) && isActiveSector && (
                <span style={{ color: '#111111' }}>{'/'}</span>
            )}
            {isActiveSector && (
                <span style={{ color: '#720092' }}>{_.get(this.props.data, 'sector.paymentIndex')}</span>
            )}
        </div>
    )

    getTextColor = (isActiveSelf, isActivePartners, isActiveClub) => {
        if (isActiveSelf) return '#27358b'
        if (isActivePartners) return '#66C1ED'
        if (isActiveClub) return '#d53868'
        return '#720092'
    }

    getIcons = (isActiveSelf, isActivePartners, isActiveClub) => (
        <div style={{ minWidth: '35px' }}>
            {isActiveSelf && <div className={cx('legend-table-column', 'icon', 'self')} />}
            {isActivePartners && <div className={cx('legend-table-column', 'icon', 'subject')} />}
            {isActiveClub && <div className={cx('legend-table-column', 'icon', 'club')} />}
        </div>
    )

    getSetup = () =>
        _.map(this.props.legend, (legendItem) => {
            const isActiveSector = _.get(this.props.data, 'sector.code') === legendItem.code
            const isActiveSelf = _.get(this.props.data, 'subject.code') === legendItem.code
            const isActivePartners = _.get(this.props.data, 'suppliers.code') === legendItem.code
            const isActiveClub = _.get(this.props.data, 'club.code') === legendItem.code
            const activeLarge = isActiveLarge(this.props.data, legendItem.code)

            return {
                ...legendItem,
                active: isActiveSector || isActiveSelf || isActivePartners || isActiveClub,
                activeLarge,
                setup: [
                    {
                        identifier: 'label',
                        style: { width: '40px', marginLeft: '5px' },
                        value: this.getIcons(isActiveSelf, isActivePartners, isActiveClub),
                        header: { label: '', style: { width: '0px' } },
                    },
                    {
                        identifier: 'icon_label',
                        style: { width: '130px', borderRight: '1px solid #4d4d4d' },
                        value: this.getP12Value(isActiveSelf, isActivePartners, isActiveClub),
                        header: { label: this.props.translates.p12label, style: { minWidth: '170px' } },
                    },
                    {
                        identifier: 'icon',
                        type: isActiveSector ? 'sector' : '',
                        style: { width: '40px', marginLeft: '5px' },
                        header: { label: '', style: { width: '0px' } },
                    },
                    {
                        identifier: 'icon_label',
                        style: {
                            width: '150px',
                            minWidth: '150px',
                            paddingRight: '3px',
                            borderRight: '1px solid #4d4d4d',
                        },
                        value: isActiveSector ? this.props.translates.p12sectorText : '',
                        header: { label: this.props.translates.p12sectorLabel, style: { minWidth: '190px' } },
                    },
                    {
                        identifier: 'thermometer',
                        style: { paddingLeft: '5px', marginLeft: '5px' },
                        header: {
                            style: { width: '0px', marginLeft: '0', paddingLeft: '8px' },
                        },
                    },
                    {
                        identifier: 'label',
                        arrow: true,
                        style: {
                            minWidth: '210px',
                            borderRight: '1px solid #4d4d4d',
                            color: this.getTextColor(
                                isActiveSelf,
                                isActivePartners,
                                isActiveClub,
                                isActiveSector,
                                legendItem.color
                            ),
                        },
                        value: this.getValue(isActiveSelf, isActivePartners, isActiveClub, isActiveSector),
                        header: {
                            label: (
                                <span>
                                    {this.props.translates.dueLabel}
                                    <CustomDate
                                        date={`${_.get(this, 'props.data.startDate.value')}`}
                                        precision={_.get(this, 'props.data.startDate.precision')}
                                        precisionFormats={PRECISION_FORMATS}
                                        customStyle={{
                                            color: 'black',
                                            fontFamily: 'Trivia Sans Regular Bold, sans-serif',
                                        }}
                                    />
                                </span>
                            ),
                            style: { minWidth: '224px' },
                        },
                    },
                    {
                        identifier: 'label',
                        style: { minWidth: '70px', width: '12%', paddingLeft: '5px', borderRight: '1px solid #4d4d4d' },
                        value: this.getInterval(legendItem),
                        header: { label: this.props.translates.interval, style: { lineHeight: '40px' } },
                    },
                    {
                        identifier: 'label',
                        style: { fontWeight: isActiveSelf ? 900 : 'auto', flex: '1 0 110px', paddingLeft: '5px' },
                        value: legendItem.description,
                        header: { label: this.props.translates.description, style: { lineHeight: '40px' } },
                    },
                ],
            }
        })

    render() {
        const { data, chartData, originalData, module, block, id } = this.props

        return (
            <div className={'payment-comparison'}>
                <LegendTable legend={this.getSetup()} />
                <div className={'sector-name-row'}>
                    <FormattedMessage
                        id={`${LOCALE_PREFIX}.sectorName`}
                        values={{
                            b: (chunks) => <b>{chunks}</b>,
                            sectorName: data?.sectorName || '',
                        }}
                    />
                </div>
                <Chart data={chartData} originalData={originalData} module={module} block={block} id={id} />
            </div>
        )
    }
}
